import React, { useState, memo } from 'react';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ReimburseeCreationDialog from './ReimburseeCreationDialog';
import { FormWrapper } from './Wrappers';
import { Reimbursee } from './Types';

type Option = { label: string, value: string, group?: string }

// A giro number as printed, the dash before its last `tail` digits. Visma stores digits only; a
// number too short to carry the dash (seen in the demo company) is shown as stored.
function giro(number: string, tail: number): string {
  const digits = number.replace(/\D/g, '')
  return digits.length > tail ? `${digits.slice(0, -tail)}-${digits.slice(-tail)}` : number
}

// Where the money goes, as the treasurer will recognise it on the payment.
function route(r: Reimbursee): string {
  if (r.BankAccountNumber) return `konto ${r.BankAccountNumber}`
  if (r.BankgiroNumber) return `bankgiro ${giro(r.BankgiroNumber, 4)}`
  if (r.PlusgiroNumber) return `plusgiro ${giro(r.PlusgiroNumber, 1)}`
  return ''
}

export function isValidReimbursee(reimburseeId: string | null) {
  return reimburseeId !== null
}

const ReimburseeSelect = memo(({label, reimbursees, value, onChange, onChangeReimbursees, id}: {
  label?: string,
  reimbursees: Reimbursee[],
  value: string | null,
  onChange: (newValue: string | null) => void,
  onChangeReimbursees: (newReimbursees: Reimbursee[]) => void,
  id?: string
}) => {

  const [dialogEnabled, setDialogEnabled] = useState(false)

  // The backend sends private persons first, then giro payees, which is the order groupBy needs.
  const lastOptions: Option[] = reimbursees.map(r => ({
    value: r.Id, label: `${r.Name} (${route(r)})`,
    group: r.BankAccountNumber ? 'Privatpersoner' : 'Företag och föreningar'}))
  const options: Option[] = [
    { value: 'NEW', label: 'Ny…'},
    ...lastOptions
  ]
  const selectedOption = options.find(option => option.value == value) ?? null

  function handleChange(option: Option | null) {
    if (option !== null && option.value == "NEW") {
      setDialogEnabled(true)
    } else {
      onChange(option === null ? null : option.value)
    }
  }

  function handleCreate({newReimbursees, newReimburseeId}: {newReimbursees: Reimbursee[], newReimburseeId: string}) {
    onChangeReimbursees(newReimbursees)
    onChange(newReimburseeId)
    setDialogEnabled(false)
  }

  function handleCancel() {
    setDialogEnabled(false)
  }

  return (
    <>
      <TheSelect
        label={label}
        value={selectedOption}
        options={options}
        onChange={(_event, newValue) => {
          handleChange(newValue)
        }}
        id={id}
      />
      {dialogEnabled && <ReimburseeCreationDialog onCreate={handleCreate} onCancel={handleCancel}/>}
    </>
  )
})

const TheSelect = FormWrapper(
  {
    Component:
    ({error, ...props}: {error: boolean, disabled: boolean, sx: {}, id?: string} & Omit<AutocompleteProps<Option, false, false, false>, 'renderInput'>) =>
      <Autocomplete<Option>
        {...props}
        autoHighlight
        groupBy={option => option.group ?? ''}
        renderInput={(params) => <TextField {...params} error={error}/>}
        renderOption={(inner_props, option: Option) => (
          <Box component="li" {...inner_props}>
            {option.value === 'NEW' ? <b>{option.label}</b> : option.label}
          </Box>
        )}
      />,
    validationFunction: (option: Option | null) => option !== null
  }
)

export default ReimburseeSelect
